import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Menu, 
  MenuItem,
  Snackbar,
  Alert,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid } from '@mui/x-data-grid';
import ToggleButtonComponent from '../../../../shared/Dashboard/ToogleButton';
import { prepareColumnsAndGroups, CustomPagination } from '../../../../shared/Helper';
import { Download } from 'lucide-react';
import CloseIcon from '@mui/icons-material/Close';
import { toLower } from 'lodash';
import axiosInstance from '../../../../utils/axios';



const TableComponent = ({
  open,
  onClose,
  type,
  setType,
  setVisibility,
  visibility,
  columns,
  data = {},
  list,
  setCurrentFilterValue,
  currentFilterValue,
  setPaginationModel,
  paginationModel,
  initialFilter,
  filters,
  setFilters,
  setList,
  setTableValue 
}) => {
  const { reports = [] } = data;
  const [currentFilter, setCurrentFilter] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const filterableColumns = [ "block", "row", "section", "scope"];

  
  // Temporary filter state for apply/cancel functionality
  const [tempFilterValues, setTempFilterValues] = useState({});
  
  const [downloadProgress, setDownloadProgress] = useState({
    isDownloading: false,
    current: 0,
    total: 0,
    downloadedCount: 0,
    failedCount: 0,
    message: ''
  });
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (setTableValue && open) {
      setLoading(true);
      const currentValues = getResult(filters);
      const visibilityValue = visibility === 'internal' ? 0 : 1;

      const fetchData = async () => {
        try {
          if (filters?.length) {
            await setTableValue({
              ...currentValues,
              type: [toLower(type)],
              visibility: [visibilityValue],
              page: paginationModel?.page + 1,
              page_size: paginationModel?.pageSize,
            }, type.toLowerCase());
          } else {
            await setTableValue({
              visibility: [visibilityValue],
              type: [toLower(type)],
              page: paginationModel?.page + 1,
              page_size: paginationModel?.pageSize,
            }, type.toLowerCase());
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [type, visibility, filters, paginationModel, setTableValue, open]);

  const getResult = (data) => {
    return data?.reduce((acc, item) => ({ ...acc, ...item }), {});
  };

  const handleOpenFilter = (event, filterType) => {
    setAnchorEl(event.currentTarget);
    setCurrentFilter(filterType);
    // Initialize temporary filter values with current filter values
    setTempFilterValues({
      [filterType]: currentFilterValue?.[filterType] || []
    });
  };

  const handleImageClick = (url) => {
    // setSelectedImage(url);
    // setOpen(true);
  }

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
  
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
  
      const blob = await response.blob();
      const blobURL = window.URL.createObjectURL(blob);
  
      const link = document.createElement('a');
      link.href = blobURL;
      link.setAttribute('download', filename);
      link.style.display = 'none';
  
      document.body.appendChild(link);
      link.click();
  
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobURL);
      }, 1000);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  };
  
  const getExtensionFromURL = (url) => {
    try {
      const pathname = new URL(url).pathname;
      const extMatch = pathname.match(/\.(\w+)(\?|$)/);
      return extMatch ? extMatch[1] : 'file';
    } catch (e) {
      return 'file';
    }
  };
  
  const handleDownloadAll = async (value) => {
    const baseResult = {
        visibility: [visibility === 'internal' ? 0 : 1],
        type: [toLower(type)],
        is_download: true,
      };
      
    const result = value ? { ...getResult(filters), ...baseResult,} : baseResult;
    
    try {
      setDownloadProgress({
        isDownloading: true,
        current: 0,
        total: 0,
        downloadedCount: 0,
        failedCount: 0,
        message: 'Preparing downloads...'
      });
      setShowToast(true);

      const res = await axiosInstance.post('/projects/get_qc_reports', result);
      const reports = res?.data?.data?.reports;
  
      if (Array.isArray(reports) && reports.length) {
        const totalFiles = reports.filter(item => item?.file_url).length;
        
        if (totalFiles === 0) {
          setDownloadProgress(prev => ({
            ...prev,
            isDownloading: false,
            message: 'No files found to download.'
          }));
          
          setTimeout(() => {
            setShowToast(false);
          }, 3000);
          return;
        }
        
        setDownloadProgress(prev => ({
          ...prev,
          total: totalFiles,
          downloadedCount: 0,
          failedCount: 0,
          message: `Starting download of ${totalFiles} files...`
        }));

        for (const [index, item] of reports.entries()) {
          if (item?.file_url) {
            const ext = getExtensionFromURL(item.file_url);
            const filename = `${item.block || 'Report'}_Section-${item.section}_Row-${item.row}_${index}.${ext}`;
            
            try {
              setDownloadProgress(prev => ({
                ...prev,
                message: `Downloading: ${filename} (${prev.downloadedCount + prev.failedCount + 1}/${totalFiles})`
              }));
              
              await downloadFile(item.file_url, filename);
              
              setDownloadProgress(prev => ({
                ...prev,
                current: prev.downloadedCount + 1,
                downloadedCount: prev.downloadedCount + 1,
                failedCount: prev.failedCount,
                message: `✅ Downloaded: ${filename} (${prev.downloadedCount + 1 + prev.failedCount}/${totalFiles})`
              }));
              
              await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (error) {
              console.error(`Failed to download file ${filename}:`, error);
              
              setDownloadProgress(prev => ({
                ...prev,
                current: prev.downloadedCount,
                downloadedCount: prev.downloadedCount,
                failedCount: prev.failedCount + 1,
                message: `❌ Failed: ${filename}. Continuing... (${prev.downloadedCount + prev.failedCount + 1}/${totalFiles})`
              }));
              
              await new Promise(resolve => setTimeout(resolve, 1500));
            }
          }
        }

        setDownloadProgress(prev => {
          const successMessage = prev.failedCount === 0 
            ? `✅ All downloads completed successfully! ${prev.downloadedCount} files downloaded.`
            : `Download completed! ${prev.downloadedCount} files downloaded successfully, ${prev.failedCount} files failed.`;
            
          return {
            ...prev,
            isDownloading: false,
            message: successMessage
          };
        });
        
        setTimeout(() => {
          setShowToast(false);
        }, 5000);
        
      } else {
        setDownloadProgress(prev => ({
          ...prev,
          isDownloading: false,
          message: 'No files found to download.'
        }));
        
        setTimeout(() => {
          setShowToast(false);
        }, 3000);
      }
    } catch (err) {
      console.error(err);
      setDownloadProgress(prev => ({
        ...prev,
        isDownloading: false,
        message: '❌ Download failed. Please try again.'
      }));
      
      setTimeout(() => {
        setShowToast(false);
      }, 5000);
    }
  
    handleDownloadClose();
  };

  const { visibleColumns, columnGroupingModel } = prepareColumnsAndGroups(columns, handleOpenFilter, filters, handleImageClick, '', '', filterableColumns);

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setTempFilterValues({});
  };

  // Handle temporary filter changes (before apply)
  const handleTempSelectAll = (filterType, isChecked) => {
    setTempFilterValues(prev => ({
      ...prev,
      [filterType]: isChecked ? [...(list[filterType] || [])] : []
    }));
  };

  const handleTempFilterChange = (filterType, value) => {
    setTempFilterValues(prev => {
      const currentValues = prev[filterType] || [];
      const isSelected = currentValues.includes(value);
      const newValues = isSelected
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [filterType]: newValues
      };
    });
  };

  const handleTempClearFilter = (filterType) => {
    setTempFilterValues(prev => ({
      ...prev,
      [filterType]: []
    }));
  };

  // Apply the temporary filter changes
  const handleApplyFilter = () => {
    const filterType = currentFilter;
    const newValues = tempFilterValues[filterType] || [];

    // Update the actual filters
    setFilters((prevFilters) => {
      const updatedFilters = [...prevFilters];
      const index = updatedFilters.findIndex((f) => f.hasOwnProperty(filterType));

      if (index !== -1) {
        updatedFilters[index] = { [filterType]: newValues };
      } else {
        updatedFilters.push({ [filterType]: newValues });
      }

      return updatedFilters;
    });

    // Update current filter values
    setCurrentFilterValue((prevFilters) => ({
      ...prevFilters,
      [filterType]: newValues,
    }));

    // Close the popover
    handleCloseFilter();
  };

  // Cancel filter changes
  const handleCancelFilter = () => {
    handleCloseFilter();
  };

  const handleClearFilters = () => {
    setCurrentFilterValue(
      {
        block: [],
        row: [],
        scope: [],
        section: [],
      }
    )
    setFilters([])
    setList(
      {
        block: [],
        row: [],
        scope: [],
        section: [],
      }
    )
  }

  const handleCloseToast = () => {
    if (!downloadProgress.isDownloading) {
      setShowToast(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0
          }}>
            <Typography fontWeight={600}>ITC/ITR Report</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={2} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <ToggleButtonComponent
                data={["ITC", "ITR"]}
                active={type}
                setActive={setType}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <ToggleButtonComponent
                data={["external", "internal"]}
                active={visibility}
                setActive={setVisibility}
              />
              <Button
                variant="outlined"
                startIcon={<Download size={16} />}
                endIcon={<ExpandMoreIcon />}
                onClick={handleDownloadClick}
                disabled={downloadProgress.isDownloading}
              >
                Download
              </Button>
              <Menu
                anchorEl={downloadAnchorEl}
                open={Boolean(downloadAnchorEl)}
                onClose={handleDownloadClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <MenuItem 
                  fontSize={18} 
                  sx={{ fontSize: 16 }} 
                  onClick={handleDownloadAll}
                  disabled={downloadProgress.isDownloading}
                >
                  Download All
                </MenuItem>
                <MenuItem 
                  fontSize={18}  
                  sx={{ fontSize: 16 }} 
                  onClick={() => handleDownloadAll(true)}
                  disabled={downloadProgress.isDownloading}
                >
                  Download Filtered Data
                </MenuItem>
              </Menu>

              <Button variant={"outlined"} onClick={handleClearFilters}>Clear Filters</Button>
            </Box>
          </Box>

          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={reports?.map((row) => ({ ...row, id: row.id }))}
              columns={visibleColumns}
              pageSizeOptions={[10, 20, 50]}
              disableRowSelectionOnClick
              disableColumnMenu
              disableSelectionOnClick
              columnGroupingModel={columnGroupingModel}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              loading={loading}
              slots={{
                pagination: CustomPagination,
              }}
              sx={{
                '& .MuiDataGrid-columnHeader[data-field="block"]': {
                  backgroundColor: filters?.find((item) => Object.keys(item).includes("block"))?.block?.length > 0 ? '#f16352' : '#f5f5f5',
                  color: filters?.find((item) => Object.keys(item).includes("block"))?.block?.length > 0 ? 'white' : '',
                },
                '& .MuiDataGrid-columnHeader[data-field="row"]': {
                  backgroundColor: filters?.find((item) => Object.keys(item).includes("row"))?.row?.length > 0 ? '#f16352' : '#f5f5f5',
                  color: filters?.find((item) => Object.keys(item).includes("row"))?.row?.length > 0 ? 'white' : '',
                },
                '& .MuiDataGrid-columnHeader[data-field="scope"]': {
                  backgroundColor: filters?.find((item) => Object.keys(item).includes("scope"))?.scope?.length > 0 ? '#f16352' : '#f5f5f5',
                  color: filters?.find((item) => Object.keys(item).includes("scope"))?.scope?.length > 0 ? 'white' : '',
                },
                '& .MuiDataGrid-columnHeader[data-field="section"]': {
                  backgroundColor: filters?.find((item) => Object.keys(item).includes("section"))?.section?.length > 0 ? '#f16352' : '#f5f5f5',
                  color: filters?.find((item) => Object.keys(item).includes("section"))?.section?.length > 0 ? 'white' : '',
                },
                '& .MuiDataGrid-footerContainer': {
                  '& .MuiTablePagination-root': {
                    display: 'none',
                  },
                },
              }}
            />
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleCloseFilter}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  width: 250,
                  height: 350,
                  overflowY: 'hidden',
                  overflowX: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
            >
              <div style={{ padding: "16px", flex: 1, overflowY: 'auto' }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div className="font-medium" style={{ marginTop: 0, fontWeight: 600 }}>Filter</div>
                  <div
                    onClick={() => handleTempClearFilter(currentFilter)}
                    style={{ cursor: "pointer", color: "blue", fontWeight: 500, fontSize: 16 }}
                  >
                    Clear
                  </div>
                </div>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={
                          (list?.[currentFilter]?.length > 0 &&
                            list?.[currentFilter]?.every((value) =>
                              tempFilterValues?.[currentFilter]?.includes(value)
                            )) || false
                        }
                        indeterminate={
                          (list?.[currentFilter]?.some((value) =>
                            tempFilterValues?.[currentFilter]?.includes(value)
                          ) &&
                            !list?.[currentFilter]?.every((value) =>
                              tempFilterValues?.[currentFilter]?.includes(value)
                            )) || false
                        }
                        onChange={(e) => handleTempSelectAll(currentFilter, e.target.checked)}
                      />
                    }
                    label={
                      <Box fontSize={16}>
                        {(list?.[currentFilter]?.length > 0 &&
                          list?.[currentFilter]?.every((value) =>
                            tempFilterValues?.[currentFilter]?.includes(value)
                          )) ? "Unselect All" : "Select All"}
                      </Box>
                    }
                  />
                  {(list?.[currentFilter]?.length ? list?.[currentFilter] : []).map((value) => (
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          size='small'
                          checked={tempFilterValues?.[currentFilter]?.includes(value) || false}
                          onChange={() => handleTempFilterChange(currentFilter, value)}
                        />
                      }
                      label={
                        <Box 
                          fontSize={16} 
                          sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '170px'
                          }}
                          title={value}
                        >
                          {value}
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </div>
              
              {/* Sticky buttons section */}
              <Box
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  backgroundColor: '#ffffff',
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelFilter}
                  sx={{ 
                    flex: 1,
                    textTransform: 'none',
                    borderColor: '#d0d0d0',
                    color: '#666',
                    '&:hover': {
                      borderColor: '#b0b0b0',
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyFilter}
                  sx={{ 
                    flex: 1,
                    textTransform: 'none',
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Popover>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Download Progress Toast/Snackbar */}
      <Snackbar 
        open={showToast} 
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={null} // Prevent auto-hide during download
        sx={{ 
          top: { xs: 90, sm: 24 },
          '& .MuiSnackbarContent-root': {
            minWidth: '450px'
          }
        }}
      >
        <Alert 
          onClose={!downloadProgress.isDownloading ? handleCloseToast : undefined} 
          severity={
            downloadProgress.isDownloading 
              ? (downloadProgress.message.includes('❌') ? "warning" : "info")
              : downloadProgress.message.includes('❌') 
                ? "error" 
                : "success"
          }
          variant="filled"
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              width: '100%'
            },
            '& .MuiAlert-action': {
              display: downloadProgress.isDownloading ? 'none' : 'flex'
            }
          }}
        >
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: downloadProgress.isDownloading ? 1 : 0,
                wordBreak: 'break-word',
                fontSize: '14px'
              }}
            >
              {downloadProgress.message}
            </Typography>
            {downloadProgress.isDownloading && downloadProgress.total > 0 && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress 
                  variant="determinate" 
                  value={((downloadProgress.downloadedCount + downloadProgress.failedCount) / downloadProgress.total) * 100}
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'white'
                    }
                  }}
                />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontWeight: 500 }}>
                  Progress: {downloadProgress.downloadedCount} successful, {downloadProgress.failedCount} failed of {downloadProgress.total} files 
                  ({Math.round(((downloadProgress.downloadedCount + downloadProgress.failedCount) / downloadProgress.total) * 100)}% processed)
                </Typography>
              </Box>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
};

export default TableComponent;